import { HariseninFooter } from "@components/harisenin"
import { Header } from "@components/header"
import { FloatingWhatsApp } from "@components/misc"
import Helmet from "@helpers/Helmet"
import { useScreenSize } from "@hooks/useScreenSize"
import { Alumnus, BannerInfo, DynamicHelmet } from "@interfaces/misc"
import { onFirstInteraction } from "@lib/functions/third-party"
import useOpenGraphImage from "@lib/helpers/open-graph"
import dynamic from "next/dynamic"
import React, { useEffect, useState } from "react"
import { VALUE_LIST } from "./const/value-list"
import { LazyMount } from "./components/LazyMount"
import { HomeBanner } from "./components/new-component/HomeBanner"
import { HomepageAlumnusRow } from "./components/new-component/HomepageAlumnusRow"
import { HomepageCommunity } from "./components/new-component/HomepageCommunity"
import { OurPrograms } from "./components/new-component/OurPrograms"
import { CtaWebapp } from "./components/new-component/CtaWebapp"

// The page re-renders on resize / scroll / interaction state; keep the static sections out of it.
const Banner = React.memo(HomeBanner)
const AlumnusRow = React.memo(HomepageAlumnusRow)
const Community = React.memo(HomepageCommunity)
const Programs = React.memo(OurPrograms)
const Cta = React.memo(CtaWebapp)

// Below-the-fold sections (and heavy libs such as framer-motion) load once they get near the viewport.
const InfiniteUserPopup = dynamic(() => import("@components/alert/InfiniteUserPopup"), {
  ssr: false,
})
const PromoSlider = dynamic(
  () => import("./components/new-component/PromoSlider").then((m) => m.PromoSlider),
  { ssr: false }
)
const HomepageProgram = dynamic(
  () => import("./components/new-component/HomepageProgram").then((m) => m.HomepageProgram),
  { ssr: false }
)
const HomepageValueV2 = dynamic(() => import("./components/new-component/HomepageValueV2"), {
  ssr: false,
})
const CompanyRow = dynamic(
  () => import("./components/new-component/CompanyRow").then((m) => m.CompanyRow),
  { ssr: false }
)
const TestimonyVideoRow = dynamic(() => import("./components/new-component/TestimonyVideoRow"), {
  ssr: false,
})
const NewTestimonialRow = dynamic(
  () => import("./components/new-component/NewTestimonialRow").then((m) => m.NewTestimonialRow),
  { ssr: false }
)
const YoutubeSlider = dynamic(
  () => import("./components/new-component/YoutubeSlider").then((m) => m.YoutubeSlider),
  { ssr: false }
)

export interface LandingPageProps {
  bannerInfo: BannerInfo | null
  meta: DynamicHelmet | null
  localBusiness: DynamicHelmet | null
  collegeOrUniversity: DynamicHelmet | null
  videoTestimonies?: any
  alumnus?: Alumnus[]
}

const LandingPage: React.FC<LandingPageProps> = ({
  bannerInfo,
  meta,
  collegeOrUniversity,
  localBusiness,
  videoTestimonies = [],
  alumnus = [],
}) => {
  const { screenWidth } = useScreenSize()
  const { imageURL } = useOpenGraphImage() // <- This custom hook here!

  const [isTransparent, setIsTransparent] = useState(true)
  const [showPopup, setShowPopup] = useState(false)

  useEffect(() => onFirstInteraction(() => setShowPopup(true)), [])

  useEffect(() => {
    window.addEventListener("scroll", trackScrolling, { passive: true })

    return () => {
      window.removeEventListener("scroll", trackScrolling)
    }
  }, [])

  function isBottom(el: HTMLElement) {
    const eleTop = el.getBoundingClientRect().top

    return eleTop < 60
  }

  const trackScrolling = () => {
    const wrappedElement = document.getElementById("hp-banner")

    if (!wrappedElement) {
      return
    }

    const isBtm = isBottom(wrappedElement)

    if (isBtm) {
      setIsTransparent(false)
    } else {
      setIsTransparent(true)
    }
  }
  return (
    <>
      <FloatingWhatsApp
        phoneNumber="+6281312117711"
        accountName="Customer Services"
        allowClickAway
        notification
        notificationDelay={30000}
      />

      {showPopup && <InfiniteUserPopup />}

      <Helmet
        title={meta?.title}
        image={imageURL}
        description={meta?.description}
        isIndexed
        schemaMarkup={[localBusiness, collegeOrUniversity]}
        tags={meta?.tags}
      />

      <Header transparent={isTransparent} isNotSticky />

      <main className="font-inter">
        <Banner info={bannerInfo} />
        <AlumnusRow data={alumnus} />
        <LazyMount>
          <PromoSlider windowWidth={screenWidth} />
        </LazyMount>
        <LazyMount
          className="min-h-[600px]"
          fallback={<h2 className="sr-only">Pilih Program Buat #JadiYangKamuMau</h2>}
        >
          <HomepageProgram />
        </LazyMount>
        <LazyMount
          className="min-h-[800px] bg-grey-f5"
          fallback={
            <>
              <h2 className="sr-only">Gapai Mimpi. Lebih Cepat. Lebih Menyenangkan.</h2>
              <ul className="sr-only">
                {VALUE_LIST.map((value) => (
                  <li key={value.title}>
                    {value.title}. {value.subCopy}
                  </li>
                ))}
              </ul>
            </>
          }
        >
          <HomepageValueV2 />
        </LazyMount>
        <LazyMount
          className="min-h-[400px]"
          fallback={<h2 className="sr-only">Perusahaan Top Merekrut Alumni Kami</h2>}
        >
          <CompanyRow />
        </LazyMount>
        {videoTestimonies.length > 0 && (
          <LazyMount
            className="min-h-[700px] bg-grey-f5"
            fallback={<h2 className="sr-only">Simak Cerita Sukses Alumni Harisenin.com</h2>}
          >
            <TestimonyVideoRow testimonies={videoTestimonies} screenWidth={screenWidth} />
          </LazyMount>
        )}
        <LazyMount
          className="min-h-[650px]"
          fallback={<h2 className="sr-only">Apa Kata Alumni Harisenin.com?</h2>}
        >
          <NewTestimonialRow screenWidth={screenWidth} />
        </LazyMount>
        <Community />
        <LazyMount
          fallback={<h2 className="sr-only">Intip Keseruan di Harisenin.com</h2>}
        >
          <YoutubeSlider screenWidth={screenWidth} />
        </LazyMount>
        <Programs />
        <Cta />
      </main>

      <HariseninFooter />
    </>
  )
}

export default LandingPage
